import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronRight, ChevronLeft, CheckCircle, 
  Home, Clock, ClipboardCheck, UtensilsCrossed, Calendar,
  Users, TrendingUp, Truck, CreditCard, Bot, Sparkles
} from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useDismissal } from '../utils/dismissals';

const TOUR_KEY = 'tour_completed';
const TOUR_DISMISS_KEY = 'welcome_tour';
const TOUR_STEP_KEY = 'tour_step';

// Function to generate tour steps with dynamic tenant name
const getTourSteps = (tenantName, primaryColor) => [
  {
    id: 'welcome',
    title: `Bienvenue sur ${tenantName}`,
    description: 'Votre application de gestion de restaurant tout-en-un. Suis ce guide rapide pour découvrir les fonctionnalités principales.',
    icon: Sparkles,
    color: `from-[${primaryColor}] to-emerald-600`,
    tips: [
      'Interface intuitive mobile-first',
      'Tous vos outils en un seul endroit',
      'Conçu pour les restaurateurs'
    ]
  },
  {
    id: 'dashboard',
    title: 'Le Dashboard',
    description: 'Votre tableau de bord affiche en temps réel le chiffre, la météo, l\'équipe présente et les KPIs importants.',
    icon: Home,
    color: 'from-blue-500 to-[var(--p-f0592b)]',
    tips: [
      'Stories en haut: chiffre, météo, équipe, HACCP',
      'Timeline d\'activité récente',
      'Accès rapide aux modules'
    ],
    route: '/dashboard'
  },
  {
    id: 'pointage',
    title: 'Pointage de l\'équipe',
    description: 'Vos employés peuvent pointer leur arrivée avec un selfie et la géolocalisation. Vous voyez en temps réel qui est présent.',
    icon: Clock,
    color: 'from-[var(--brand)] to-[var(--p-f0592b)]',
    tips: [
      'Selfie obligatoire avec uniforme',
      'Vérification GPS (30m du restaurant)',
      'Calcul automatique des retards'
    ],
    route: '/pointage'
  },
  {
    id: 'haccp',
    title: 'Checklists HACCP',
    description: 'Complétez vos checklists d\'ouverture et fermeture. Relevez les températures. Tout est tracé et archivé.',
    icon: ClipboardCheck,
    color: 'from-orange-500 to-orange-600',
    tips: [
      'Checklist ouverture & fermeture',
      'Relevés de température',
      'Signature responsable'
    ],
    route: '/checklists'
  },
  {
    id: 'fiches',
    title: 'Fiches Techniques',
    description: 'Créez vos recettes avec les ingrédients et quantités. Le Coût matière est calculé automatiquement.',
    icon: UtensilsCrossed,
    color: 'from-red-500 to-[var(--p-f0592b)]',
    tips: [
      'Calcul automatique du coût matière',
      'Indicateur de rentabilité',
      'Suggestions de Sabril pour optimiser'
    ],
    route: '/fiches'
  },
  {
    id: 'planning',
    title: 'Planning de l\'équipe',
    description: 'Planifiez les horaires de votre équipe. L\'Sabril peut vous suggérer un planning basé sur la météo et l\'affluence prévue.',
    icon: Calendar,
    color: 'from-cyan-500 to-blue-600',
    tips: [
      'Vue calendrier semaine',
      'Suggestions de Sabril selon la météo',
      'Calcul des heures et salaires'
    ],
    route: '/planning'
  },
  {
    id: 'fournisseurs',
    title: 'Fournisseurs & Coût matière',
    description: 'Importez vos produits fournisseurs et liez-les à vos ingrédients. Le Coût matière de vos recettes sera mis à jour automatiquement.',
    icon: Truck,
    color: 'from-orange-500 to-red-600',
    tips: [
      'Import factures Foodflow/Transgourmet',
      'Liaison produit → ingrédient',
      'Calcul prix au gramme automatique'
    ],
    route: '/articles-achetes'
  },
  {
    id: 'ai',
    title: 'Consultant',
    description: 'Sabril est disponible 24/7 via la bulle en bas de l\'écran. Posez-lui toutes vos questions sur la gestion de votre restaurant.',
    icon: Bot,
    color: `from-[${primaryColor}] to-emerald-600`,
    tips: [
      'Analyse de votre chiffre et rentabilité',
      'Conseils marketing et RH',
      'Optimisation des coûts'
    ]
  },
  {
    id: 'done',
    title: 'C\'est parti !',
    description: `Vous êtes prêt à utiliser ${tenantName}. N'hésitez pas à explorer chaque module. Le consultant est là pour vous aider.`,
    icon: CheckCircle,
    color: `from-[${primaryColor}] to-emerald-600`,
    tips: [
      'Commencez par ajouter vos employés',
      'Configurez vos fiches techniques',
      'Importez vos produits fournisseurs'
    ]
  }
];

export const OnboardingTour = ({ onComplete, forceShow = false }) => {
  const { tenant } = useTenant();
  const tenantName = tenant?.name || 'votre espace';
  const primaryColor = tenant?.branding?.primary_color || 'var(--brand)';
  
  const TOUR_STEPS = getTourSteps(tenantName, primaryColor);
  
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const interacted = useRef(false);
  useEffect(() => {
    const mark = () => { interacted.current = true; };
    document.addEventListener('pointerdown', mark, {once:true});
    document.addEventListener('wheel', mark, {once:true,passive:true});
    return () => {document.removeEventListener('pointerdown',mark);document.removeEventListener('wheel',mark);};
  }, []);
  const { ready: dismissReady, dismissed: serverDismissed, dismiss: serverDismiss } = useDismissal(TOUR_DISMISS_KEY);

  useEffect(() => {
    if (!dismissReady && !forceShow) return;
    // Check if tour was already completed (server-side memory first, legacy localStorage as fallback)
    const completed = serverDismissed || localStorage.getItem(TOUR_KEY);
    const savedStep = localStorage.getItem(TOUR_STEP_KEY);
    
    if (forceShow || (!completed && !interacted.current)) {
      setIsVisible(true);
      if (savedStep) {
        setCurrentStep(parseInt(savedStep, 10));
      }
    } else {
      setIsVisible(false);
    }
  }, [forceShow, dismissReady, serverDismissed]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      localStorage.setItem(TOUR_STEP_KEY, nextStep.toString());
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      localStorage.setItem(TOUR_STEP_KEY, prevStep.toString());
    }
  };

  const handleComplete = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    localStorage.removeItem(TOUR_STEP_KEY);
    serverDismiss('forever');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    localStorage.removeItem(TOUR_STEP_KEY);
    serverDismiss('forever');
    setIsVisible(false);
    onComplete?.();
  };

  const handleLater = () => {
    serverDismiss('later');
    setIsVisible(false);
  };

  useEffect(() => {
    if (!isVisible) return;
    const escape = e => { if (e.key === 'Escape') { serverDismiss('later'); setIsVisible(false); } };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [isVisible, serverDismiss]);

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="welcome-tour-modal"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-[var(--carte)] rounded-[28px] w-full max-w-md max-h-[90dvh] overflow-y-auto shadow-2xl border border-[var(--bord)]"
        >
          {/* Progress bar */}
          <div className="h-1 bg-[var(--remplissage)]">
            <motion.div 
              className="h-full bg-[var(--brand)]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Header */}
          <div className="p-6 pb-2 text-[var(--texte)]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--brand)]">
                Étape {currentStep + 1} / {TOUR_STEPS.length}
              </span>
              <div className="flex items-center gap-3 text-sm">
                <button 
                  onClick={handleLater}
                  data-testid="tour-later-btn"
                  className="text-[var(--texte-2)] hover:text-[var(--texte)]"
                >
                  Plus tard
                </button>
                <button 
                  onClick={handleSkip}
                  data-testid="tour-skip-btn"
                  className="text-[var(--brand)] font-semibold"
                >
                  Ne plus me montrer
                </button>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[var(--brand-soft)] text-[var(--brand)] rounded-2xl flex items-center justify-center">
                <Icon size={28} />
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>{step.title}</h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-[var(--texte-2)] mb-4">
              {step.description}
            </p>

            {/* Tips */}
            <div className="space-y-2 mb-6">
              {step.tips.map((tip, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[var(--brand)] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[var(--texte)]">{tip}</span>
                </div>
              ))}
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mb-6">
              {TOUR_STEPS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentStep(idx);
                    localStorage.setItem(TOUR_STEP_KEY, idx.toString());
                  }}
                  data-testid={`tour-dot-${idx}`}
                  aria-label={`Aller à l\u2019étape ${idx + 1}`}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentStep 
                      ? 'bg-[var(--brand)] w-6' 
                      : idx < currentStep 
                        ? 'bg-[var(--brand)]/50' 
                        : 'bg-[var(--remplissage)]'
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  data-testid="tour-prev-btn"
                  className="flex-1 py-3 border border-[var(--bord)] rounded-xl font-medium text-[var(--texte)] flex items-center justify-center gap-2"
                >
                  <ChevronLeft size={20} />
                  Précédent
                </button>
              )}
              <button
                onClick={handleNext}
                data-testid="tour-next-btn"
                className="flex-1 py-3 bg-[var(--brand)] text-[var(--sur-couleur)] rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {currentStep === TOUR_STEPS.length - 1 ? (
                  <>
                    Commencer
                    <Sparkles size={18} />
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to reset tour (for testing or re-showing)
export const useOnboardingTour = () => {
  const resetTour = () => {
    localStorage.removeItem(TOUR_KEY);
    localStorage.removeItem(TOUR_STEP_KEY);
  };

  const isTourCompleted = () => {
    return localStorage.getItem(TOUR_KEY) === 'true';
  };

  return { resetTour, isTourCompleted };
};

export default OnboardingTour;
